import React, { useState, useEffect, useRef } from 'react'
import { XMarkIcon, CameraIcon } from '@heroicons/react/24/outline'
import { Html5Qrcode } from 'html5-qrcode'
import { toast } from 'react-hot-toast'
import { usePopup } from '@/contexts/PopupContext'

interface QRCodeScannerPopupProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Component quét mã QR cho nhân viên
 * 
 * Chức năng:
 * - Quét mã QR từ camera thiết bị
 * - Tự động nhận diện và xử lý mã QR
 * - Chỉ chấp nhận URL từ domain pconnext.io và app.pango.vn
 * - Tự động chuyển hướng khi quét thành công
 * 
 * Quy trình:
 * 1. Kiểm tra và xin quyền truy cập camera
 * 2. Khởi tạo scanner khi có quyền
 * 3. Quét liên tục với tốc độ 10fps
 * 4. Khi phát hiện mã QR:
 *    - Kiểm tra có phải URL hợp lệ không
 *    - Kiểm tra domain có được phép không
 *    - Nếu hợp lệ: Hiển thị thông báo thành công và chuyển hướng
 *    - Nếu không hợp lệ: Hiển thị thông báo lỗi và tiếp tục quét
 * 5. Dọn dẹp tài nguyên khi đóng popup
 * 
 * Lưu ý:
 * - Cần xử lý các trường hợp lỗi camera
 * - Đảm bảo dừng scanner đúng cách khi đóng
 * - Tối ưu hiệu suất quét với fps và kích thước vùng quét
 * - Thêm loading state để UX mượt mà
 * - Có thể bật/tắt log lỗi khi debug bằng DEBUG_MODE
 */

export const QRCodeScannerPopup: React.FC<QRCodeScannerPopupProps> = ({ isOpen, onClose }) => {
  const { openPopup, closePopup } = usePopup()
  const prevIsOpen = React.useRef(isOpen)
  const [permission, setPermission] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [isScanning, setIsScanning] = useState<boolean>(false)
  const scannerRef = useRef<Html5Qrcode | null>(null)

  // Kiểm tra quyền truy cập camera
  useEffect(() => {
    if (!isOpen) return

    let mounted = true

    navigator.mediaDevices.getUserMedia({ video: true })
      .then(() => {
        if (!mounted) return
        setPermission(true)
        setLoading(false)
      })
      .catch((error) => {
        console.error('Lỗi camera:', error)
        if (!mounted) return
        setPermission(false)
        setLoading(false)
      })

    return () => {
      mounted = false
    }
  }, [isOpen])

  // Dừng scanner an toàn
  const stopScanner = async () => {
    if (scannerRef.current && isScanning) {
      try {
        await scannerRef.current.stop()
        await scannerRef.current.clear()
        scannerRef.current = null
        setIsScanning(false)
      } catch (error) {
        if (DEBUG_MODE) {
          console.error('Lỗi khi dừng scanner:', error)
        }
      }
    }
  }

  // Đóng popup an toàn
  const handleClose = async () => {
    await stopScanner()
    onClose()
  }

  // Xử lý URL từ mã QR
  const handleQRCode = async (decodedText: string) => {
    try {
      const url = new URL(decodedText)
      const allowedDomains = ['pconnext.io', 'app.pango.vn']
      
      if (allowedDomains.includes(url.hostname)) {
        toast.success('Quét mã QR thành công!')
        await stopScanner()
        onClose()
        window.location.href = decodedText
      } else {
        toast.error('Mã QR không hợp lệ!')
        if (DEBUG_MODE) {
          console.log('Domain không được phép:', url.hostname)
        }
      }
    } catch {
      toast.error('Mã QR không hợp lệ!')
      if (DEBUG_MODE) {
        console.log('URL không hợp lệ:', decodedText)
      }
    }
  }

  // Khởi tạo scanner khi có quyền truy cập camera
  useEffect(() => {
    if (!isOpen || !permission || loading) return

    const qrReaderElement = document.getElementById('qr-reader')
    if (!qrReaderElement) {
      if (DEBUG_MODE) {
        console.error('Không tìm thấy element qr-reader')
      }
      return
    }

    let mounted = true

    const initializeScanner = async () => {
      try {
        const qrScanner = new Html5Qrcode('qr-reader')
        if (!mounted) {
          await qrScanner.clear()
          return
        }

        scannerRef.current = qrScanner
        setIsScanning(true)

        await qrScanner.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1
          },
          handleQRCode,
          (error: string) => {
            // Chỉ log lỗi trong chế độ debug
            if (DEBUG_MODE && !error.includes('NotFoundException') && !error.includes('No barcode or QR code detected')) {
              console.error('Lỗi khi quét:', error)
            }
          }
        )
      } catch (err) {
        if (DEBUG_MODE) {
          console.error('Lỗi khi khởi tạo scanner:', err)
        }
        if (!mounted) return
        setPermission(false)
        setLoading(false)
      }
    }

    // Đợi một chút để đảm bảo element đã được render
    const timeoutId = setTimeout(initializeScanner, 100)

    return () => {
      mounted = false
      clearTimeout(timeoutId)
      stopScanner()
    }
  }, [isOpen, permission, loading, onClose])

  React.useEffect(() => {
    if (!prevIsOpen.current && isOpen) {
      openPopup()
    }
    if (prevIsOpen.current && !isOpen) {
      closePopup()
    }
    prevIsOpen.current = isOpen
  }, [isOpen])

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50"
        onClick={handleClose}
      />
      
      {/* Popup */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:w-[420px] w-[90%] max-h-[80vh] bg-white rounded-2xl shadow-2xl z-50">
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="pr-8">
            <h2 className="text-lg font-semibold text-gray-900">
              Quét mã QR
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Di chuyển camera đến mã QR để quét
            </p>
          </div>
          
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 p-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-colors duration-200"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400" />
              <p className="mt-4">Đang khởi tạo camera...</p>
            </div>
          ) : !permission ? (
            <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
              <CameraIcon className="w-12 h-12" />
              <p className="mt-4 text-center">
                Không thể truy cập camera.<br />
                Vui lòng cấp quyền và thử lại.
              </p>
            </div>
          ) : (
            <div 
              id="qr-reader" 
              className="overflow-hidden rounded-xl"
              style={{ 
                width: '100%',
                height: '300px'
              }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100">
          <div className="text-xs text-gray-400 text-center">
            Chỉ chấp nhận mã QR từ hệ thống Pango
          </div>
        </div>
      </div>
    </>
  )
} 